/**
 * NOBL Air cart drawer upsell
 * File: assets/nobl-air-upsell.js
 *
 * Upgrades the paid NOBL Air Kaching bundle in the cart drawer to the next
 * tier (1 -> 3, 3 -> 5). The old bundle line(s) are removed and ONE new line
 * is added, shaped exactly like the PDP's Kaching add for that tier:
 *   __kaching_bundles = {deal, pp, main, v, id, bid}
 *   + _nobl_offer = b3g2 for the 5-unit tier
 * Kaching's Shopify discount then prices it ($18 / $27). Gifts for the 5-unit
 * tier are NOT added here - free-gift.liquid's GiftManager adds them when it
 * sees _nobl_offer=b3g2 after we publish cartUpdate.
 *
 * Tracking: pushes dataLayer events
 *   nobl_air_upsell_viewed / _clicked / _applied / _failed
 * and stores "from>to" in the _nobl_air_upsell cart attribute (saved on the order).
 */
(function () {
  'use strict';

  if (window.NOBLAirUpsellLoaded) return;
  window.NOBLAirUpsellLoaded = true;

  const KACHING_PROP = '__kaching_bundles';
  const OFFER_PROP = '_nobl_offer';
  const OFFER_VALUE = 'b3g2';
  const OFFER_MIN_UNITS = 5;
  const ERROR_TEXT = 'Something went wrong. Please try again.';

  // Same regions GiftManager repaints, PLUS the upsell root it deliberately skips.
  const DRAWER_REGIONS = [
    '.drawer__header',
    '.cart--drawer__items',
    '.cart__free-gift--outer',
    'free-shipping-bar',
    '.drawer__footer',
    '[data-cart-upsell-root]'
  ];

  const SHOP_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartUrl = () => (window.routes && window.routes.cart_url) || SHOP_ROOT + 'cart';

  let busy = false;
  const viewed = new Set();

  // ---------- helpers ----------

  function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, nobl_air_upsell: data });
    document.dispatchEvent(new CustomEvent('nobl-air-upsell:' + event, { detail: data }));
  }

  function payloadFrom(el) {
    if (el.hasAttribute('data-nau-powerbank')) {
      return { offer: 'power_bank', original_tier: 5, final_tier: 5, product_id: el.dataset.nauPowerbank };
    }
    return {
      offer: el.dataset.nauOfferName,
      trigger: el.dataset.nauTrigger || 'card',
      original_tier: parseInt(el.dataset.nauFrom, 10),
      final_tier: parseInt(el.dataset.nauTo, 10),
      added_value: (parseInt(el.dataset.nauPrice, 10) || 0) / 100
    };
  }

  function randomId(len) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let out = '';
    for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  async function fetchCart() {
    const res = await fetch(SHOP_ROOT + 'cart.js?_=' + Date.now(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) throw new Error('cart.js HTTP ' + res.status);
    return res.json();
  }

  async function postJSON(path, body) {
    const res = await fetch(SHOP_ROOT + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'HTTP ' + res.status);
    return data;
  }

  // Paid NOBL Air lines added by Kaching on the PDP. Free NOBL Air units
  // (luggage gifts etc.) never carry __kaching_bundles, so they are ignored.
  function getBundleLines(cart, productId) {
    return (cart.items || []).filter(
      (item) => String(item.product_id) === String(productId) && item.properties && item.properties[KACHING_PROP]
    );
  }

  const sumQty = (lines) => lines.reduce((total, line) => total + line.quantity, 0);

  // Copies the PDP's Kaching shape for the target tier.
  function buildProperties(line, targetQty) {
    const props = Object.assign({}, line.properties || {});
    let kaching = {};
    try { kaching = JSON.parse(props[KACHING_PROP]) || {}; } catch (e) { kaching = {}; }

    const next = {};
    ['deal', 'pp', 'main', 'v'].forEach((key) => {
      if (kaching[key] !== undefined) next[key] = kaching[key];
    });
    if (next.main === undefined) next.main = true;
    next.id = randomId(4);
    next.bid = uuid();

    props[KACHING_PROP] = JSON.stringify(next);
    if (targetQty >= OFFER_MIN_UNITS) props[OFFER_PROP] = OFFER_VALUE;
    else delete props[OFFER_PROP];
    return props;
  }

  // Let GiftManager / cart.js know the cart changed under them.
  function markWrite() {
    window.NOBLCartSync = window.NOBLCartSync || {};
    window.NOBLCartSync.writeSeq = (window.NOBLCartSync.writeSeq || 0) + 1;
    window.NOBLCartSync.lastCart = null;
  }

  async function waitForOtherCartWork() {
    try {
      if (window.NOBLCartSync && typeof window.NOBLCartSync.waitForAdds === 'function') {
        await window.NOBLCartSync.waitForAdds();
      }
      if (window.GiftManager && typeof window.GiftManager.waitForIdle === 'function') {
        await window.GiftManager.waitForIdle();
      }
    } catch (e) { /* never block the upgrade on this */ }
  }

  function setCheckoutBusy(on) {
    if (window.GiftManager && typeof window.GiftManager._setCheckoutBusy === 'function') {
      window.GiftManager._setCheckoutBusy(on);
    }
  }

  function updateBubble(itemCount) {
    if (window.GiftManager && typeof window.GiftManager._updateBubble === 'function') {
      window.GiftManager._updateBubble(itemCount);
      return;
    }
    const bubble = document.querySelector('.cart-count-bubble');
    if (!bubble) return;
    bubble.style.display = itemCount === 0 ? 'none' : 'block';
    const span = bubble.querySelector('span');
    if (span) span.textContent = itemCount;
  }

  // Re-render the open drawer in place (it never closes). Uses the same shared
  // version counter as GiftManager / cart.js so a stale paint can't win.
  async function refreshDrawer(cart) {
    window.NOBLCartDrawerSync = window.NOBLCartDrawerSync || { version: 0 };
    const token = ++window.NOBLCartDrawerSync.version;

    const res = await fetch(cartUrl() + '?section_id=cart-drawer', { credentials: 'same-origin' });
    const html = await res.text();
    if (token !== window.NOBLCartDrawerSync.version) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    DRAWER_REGIONS.forEach((selector) => {
      const next = doc.querySelector(selector);
      const current = document.querySelector(selector);
      if (next && current) current.replaceWith(next);
    });

    if (cart) updateBubble(cart.item_count);
  }

  function publishCartUpdate(cart, source) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: source || 'nobl-air-upsell', cartData: cart });
      }
    } catch (e) {
      console.warn('[NOBL Air upsell] publish failed:', e);
    }
    // Re-inits the existing upsell slider / swatches (cart-upsell-list.js).
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }

  function setLoading(btn, on) {
    if (!btn) return;
    btn.disabled = on;
    btn.classList.toggle('loading', on);
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    const spinner = btn.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
  }

  function showError(message) {
    const el = document.querySelector('[data-nau-offer] [data-nau-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  // Put the original bundle back if the add failed after the removal.
  async function restore(lines) {
    try {
      await postJSON('cart/add.js', {
        items: [{ id: lines[0].variant_id, quantity: sumQty(lines), properties: lines[0].properties }]
      });
    } catch (e) {
      console.error('[NOBL Air upsell] restore failed:', e);
    }
  }

  // ---------- upgrade ----------
  // The CTA is the theme's normal add-to-cart form (product-form). Right
  // before it submits we (1) give the Kaching property a fresh bundle id and
  // (2) remove the current bundle line(s) in ONE quick request. Then the
  // normal product-form does the add + drawer render + cartUpdate (GiftManager).

  let pending = null;
  let pendingTimer = null;

  function clearPending() {
    pending = null;
    busy = false;
    clearTimeout(pendingTimer);
  }

  function buildKachingValue(currentJson, includeId) {
    let current = {};
    try { current = JSON.parse(currentJson || '{}') || {}; } catch (e) { current = {}; }
    const next = {};
    ['deal', 'pp', 'main', 'v'].forEach((key) => {
      if (current[key] !== undefined) next[key] = current[key];
    });
    if (next.main === undefined) next.main = true;
    if (includeId !== false) next.id = randomId(4);
    next.bid = uuid();
    return JSON.stringify(next);
  }

  async function removeCurrentBundle(offerEl) {
    let keys = [];
    try { keys = JSON.parse(offerEl.dataset.nauKeys || '[]'); } catch (e) { keys = []; }
    if (!keys.length) return;
    const updates = {};
    keys.forEach((key) => { updates[key] = 0; });
    markWrite();
    await postJSON('cart/update.js', {
      updates,
      attributes: { _nobl_air_upsell: offerEl.dataset.nauFrom + '>' + offerEl.dataset.nauTo }
    });
  }

  async function prepareUpgrade(form, offerEl) {
    const data = payloadFrom(offerEl);
    const btn = form.querySelector('[type="submit"]');
    track('nobl_air_upsell_clicked', data);
    showError('');
    setLoading(btn, true);
    themeLoadingBar(true); // CartItems ends it after product-form's normal re-render

    const kachingInput = form.querySelector('[data-nau-prop-kaching]');
    if (kachingInput) kachingInput.value = buildKachingValue(offerEl.dataset.nauKaching);

    try {
      await removeCurrentBundle(offerEl);
    } catch (err) {
      console.error('[NOBL Air upsell] could not remove current bundle:', err);
      track('nobl_air_upsell_failed', Object.assign({}, data, { error: String((err && err.message) || err) }));
      setLoading(btn, false);
      showError(ERROR_TEXT);
      clearPending();
      themeLoadingBar(false);
      return;
    }

    pending = {
      data,
      restoreLines: [{
        variant_id: form.querySelector('[name="id"]').value,
        quantity: data.original_tier,
        properties: { [KACHING_PROP]: offerEl.dataset.nauKaching }
      }]
    };
    pendingTimer = setTimeout(clearPending, 15000);

    // Hand over to the theme's product-form. Keep the spinner showing;
    // only re-enable the button so the form can submit it.
    btn.disabled = false;
    form.dataset.nauReady = 'true';
    if (typeof form.requestSubmit === 'function') form.requestSubmit(btn);
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  function watchCartEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    // Auto-upgrade when the drawer quantity goes UP to 2 or 4.
    subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartEvent);

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!pending || !event || event.source !== 'product-form') return;
      const { data } = pending;
      clearPending();
      track('nobl_air_upsell_applied', data);
    });

    // Add failed after the old bundle was removed: put it back.
    subscribe(PUB_SUB_EVENTS.cartError, async (event) => {
      if (!pending || !event || event.source !== 'product-form') return;
      const { data, restoreLines } = pending;
      clearPending();
      track('nobl_air_upsell_failed', Object.assign({}, data, { error: 'add_failed' }));
      await restore(restoreLines);
      const cart = await fetchCart().catch(() => null);
      await refreshDrawer(cart).catch(() => {});
      if (cart) publishCartUpdate(cart);
      showError(ERROR_TEXT);
      themeLoadingBar(false);
    });
  }

  // ---------- auto-upgrade on quantity increase ----------
  // 1 -> 2 becomes 3 (2 paid + 1 free), 3 -> 4 becomes 5 (+ gifts via GiftManager).
  // Only on an INCREASE made with the cart's own quantity controls (source 'cart-items').
  // Decreases (3 -> 2, 5 -> 4) are left alone; the upsell card still offers the free unit.

  let lastState = null;

  function upsellRoot() {
    return document.querySelector('[data-cart-upsell-root]');
  }

  function isEnabled() {
    const root = upsellRoot();
    return !!root && root.dataset.nauEnabled === 'true';
  }

  let cachedAirIds = [];
  function airIds() {
    const root = upsellRoot();
    if (root && root.dataset.nauAirIds) {
      try {
        const ids = JSON.parse(root.dataset.nauAirIds).map(String);
        if (ids.length) cachedAirIds = ids;
      } catch (e) { /* keep cached */ }
    }
    return cachedAirIds;
  }

  // Same rule as the Liquid: paid Kaching lines of the FIRST matched NOBL Air product.
  function bundleState(cart) {
    const ids = airIds();
    let productId = null;
    const lines = [];
    (cart.items || []).forEach((item) => {
      if (!item.properties || !item.properties[KACHING_PROP]) return;
      const pid = String(item.product_id);
      if (!ids.includes(pid)) return;
      if (productId === null) productId = pid;
      if (pid === productId) lines.push(item);
    });
    return { productId, qty: sumQty(lines), lines };
  }

  function readInitialState() {
    const root = upsellRoot();
    if (!root) return null;
    return {
      productId: root.dataset.nauProductId || null,
      qty: parseInt(root.dataset.nauQty, 10) || 0,
      lines: []
    };
  }

  async function handleCartEvent(event) {
    if (!isEnabled()) return;
    // Our own updates already set lastState.
    if (event && event.source === 'nobl-air-upsell') return;

    const prev = lastState;
    let cart = event && event.cartData && Array.isArray(event.cartData.items) ? event.cartData : null;
    if (!cart) cart = await fetchCart().catch(() => null);
    if (!cart) return;

    const next = bundleState(cart);
    lastState = next;

    if (!event || event.source !== 'cart-items') return;   // only the cart's qty controls
    if (busy || !prev || !next.productId) return;
    if (prev.productId && prev.productId !== next.productId) return;
    if (next.qty <= prev.qty) return;                        // only on increase
    if (next.qty !== 2 && next.qty !== 4) return;

    const to = next.qty === 2 ? 3 : 5;
    // Give GiftManager / cart.js a moment to start their own follow-up work first.
    setTimeout(() => autoUpgrade(next.productId, next.qty, to), 150);
  }

  async function autoUpgrade(productId, from, to) {
    if (busy) return;
    busy = true;
    const data = { offer: to === 3 ? 'b2g1' : 'b3g2', original_tier: from, final_tier: to, trigger: 'quantity' };
    setCheckoutBusy(true);

    try {
      await waitForOtherCartWork();

      // Re-check against the live cart: abort if the customer changed it again meanwhile.
      const cart = await fetchCart();
      const current = bundleState(cart);
      if (current.productId !== productId || current.qty !== from) {
        lastState = current;
        return;
      }

      const lines = current.lines;
      const properties = buildProperties(lines[0], to);
      markWrite();

      const updates = {};
      lines.forEach((line) => { updates[line.key] = 0; });
      await postJSON('cart/update.js', {
        updates,
        attributes: { _nobl_air_upsell: from + '>' + to }
      });

      try {
        await postJSON('cart/add.js', {
          items: [{ id: lines[0].variant_id, quantity: to, properties }]
        });
      } catch (addErr) {
        await restore(lines);
        throw addErr;
      }

      const finalCart = await fetchCart();
      lastState = bundleState(finalCart);
      await refreshDrawer(finalCart);
      publishCartUpdate(finalCart); // GiftManager adds/removes gifts from this
      track('nobl_air_upsell_auto_applied', Object.assign({}, data, { final_tier: lastState.qty }));
    } catch (err) {
      console.error('[NOBL Air upsell] auto-upgrade failed:', err);
      track('nobl_air_upsell_failed', Object.assign({}, data, { error: String((err && err.message) || err) }));
      const cart = await fetchCart().catch(() => null);
      if (cart) {
        lastState = bundleState(cart);
        await refreshDrawer(cart).catch(() => {});
        publishCartUpdate(cart);
      }
    } finally {
      busy = false;
      setCheckoutBusy(false);
    }
  }

  // ---------- view tracking ----------

  function checkViews() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return;

    document.querySelectorAll('[data-nau-offer], [data-nau-powerbank]').forEach((el) => {
      const data = payloadFrom(el);
      const key = data.offer + ':' + data.original_tier;
      if (viewed.has(key)) return;
      viewed.add(key);
      track('nobl_air_upsell_viewed', data);
    });
  }

  let viewTimer = null;
  const scheduleViewCheck = () => {
    clearTimeout(viewTimer);
    viewTimer = setTimeout(checkViews, 250);
  };

  // ---------- events ----------

  // Drawer "+" on the NOBL Air bundle line at 1 or 3 units: instead of the
  // theme's plain +1 (which Kaching would just charge for), run the upsell
  // card's upgrade (1 -> 3, 3 -> 5) through its normal add-to-cart.
  // Minus, other products and other quantities are untouched.
  document.addEventListener('click', (event) => {
    const plus = event.target.closest('button[name="plus"]');
    if (!plus) return;

    const qtyWrapper = plus.closest('quantity-input');
    const input = qtyWrapper && qtyWrapper.querySelector('.quantity__input[data-key]');
    const offerEl = document.querySelector('[data-nau-offer]');
    if (!input || !offerEl) return;

    const from = parseInt(offerEl.dataset.nauFrom, 10);
    if (from !== 1 && from !== 3) return;

    let keys = [];
    try { keys = JSON.parse(offerEl.dataset.nauKeys || '[]'); } catch (e) { keys = []; }
    if (!keys.includes(input.dataset.key)) return; // not the NOBL Air bundle line

    const form = offerEl.querySelector('form');
    const submit = form && form.querySelector('[type="submit"]');
    if (!form || !submit) return;

    // Stop the theme's quantity +1 from running at all.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;

    offerEl.dataset.nauTrigger = 'quantity';
    if (typeof form.requestSubmit === 'function') form.requestSubmit(submit);
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }, true);

  // ---------- step down a tier on "−" ----------
  // Drawer "−" on the NOBL Air bundle line at 5 or 3 units moves to the
  // previous tier (5 -> 3, 3 -> 1) instead of a plain -1, which Kaching would
  // still charge the same for. 1 -> 0 and everything else is the theme's normal.

  // Drive the theme's own <loading-bar> (cart.js listens for these on document).
  function themeLoadingBar(on) {
    document.dispatchEvent(new CustomEvent(on ? 'theme:loading:start' : 'theme:loading:end', {
      bubbles: true,
      cancelable: true
    }));
  }

  // Instant feedback on "−": show the new quantity and dim the items while we work.
  function setStepDownBusy(on, input, to) {
    const items = document.querySelector('.cart--drawer__items');
    if (on) {
      if (input) input.value = to;
      if (items) {
        items.style.transition = 'opacity 0.15s ease';
        items.style.opacity = '0.5';
        items.style.pointerEvents = 'none';
      }
    } else if (items) {
      items.style.opacity = '';
      items.style.pointerEvents = '';
    }
  }

  function swapDrawerHTML(html) {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    DRAWER_REGIONS.forEach((selector) => {
      const next = doc.querySelector(selector);
      const current = document.querySelector(selector);
      if (next && current) current.replaceWith(next);
    });
    return true;
  }

  async function stepDown(root, keys, from, to) {
    busy = true;
    const data = { offer: to === 3 ? 'b2g1' : 'single', original_tier: from, final_tier: to, trigger: 'quantity_down' };
    const variantId = root.dataset.nauVariantId;
    const currentKaching = root.dataset.nauKaching;
    const productId = root.dataset.nauProductId;

    // What to put back if the add fails after the removal.
    const originalProps = { [KACHING_PROP]: currentKaching };
    if (from >= OFFER_MIN_UNITS) originalProps[OFFER_PROP] = OFFER_VALUE;
    const restoreLines = [{ variant_id: variantId, quantity: from, properties: originalProps }];

    let removed = false;
    let added = false;
    setCheckoutBusy(true);
    window.NOBLCartDrawerSync = window.NOBLCartDrawerSync || { version: 0 };

    try {
      // 3-unit line keeps Kaching's "id"; 1-unit line matches the PDP's single shape (no id).
      // Neither gets _nobl_offer, so GiftManager removes the 5-unit gifts.
      const properties = { [KACHING_PROP]: buildKachingValue(currentKaching, to > 1) };

      markWrite();
      const writeSeqAfter = window.NOBLCartSync.writeSeq;
      const updates = {};
      keys.forEach((key) => { updates[key] = 0; });

      // 5 -> 3: drop the NOBL gifts in this same request, so GiftManager
      // doesn't need its own extra request + drawer render afterwards.
      if (from >= OFFER_MIN_UNITS && to < OFFER_MIN_UNITS && window.GIFT_CONFIG && window.GIFT_CONFIG.giftIds) {
        const giftIds = window.GIFT_CONFIG.giftIds;
        [giftIds.airTagHolder, giftIds.airGiftCard].forEach((id) => {
          if (id) updates[id] = 0;
        });
      }

      await postJSON('cart/update.js', {
        updates,
        attributes: { _nobl_air_upsell: from + '>' + to }
      });
      removed = true;

      // Add the lower tier and get the drawer HTML back in the SAME response.
      const token = ++window.NOBLCartDrawerSync.version;
      const response = await postJSON('cart/add.js', {
        items: [{ id: variantId, quantity: to, properties }],
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      });
      added = true;

      const html = response.sections && response.sections['cart-drawer'];
      if (token === window.NOBLCartDrawerSync.version && !swapDrawerHTML(html)) {
        await refreshDrawer();
      }

      lastState = { productId, qty: to, lines: [] };
      track('nobl_air_upsell_stepped_down', data);

      // Off the critical path: the drawer is already painted. Then hand the REAL
      // cart to GiftManager/theme so they verify without another /cart.js fetch.
      fetchCart().then((cart) => {
        updateBubble(cart.item_count);
        lastState = bundleState(cart);
        // Keep cart.js's combined quantity+gift fast path working for the next click
        // (only if nothing else has written to the cart since our requests).
        if (window.NOBLCartSync && window.NOBLCartSync.writeSeq === writeSeqAfter) {
          window.NOBLCartSync.lastCart = { cart, seq: writeSeqAfter, at: Date.now() };
        }
        // 'cart-items': CartItems skips its own re-fetch + second loading bar (we already
        // painted those regions); GiftManager still verifies gifts quietly with this cart.
        publishCartUpdate(cart, 'cart-items');
      }).catch(() => {});
    } catch (err) {
      console.error('[NOBL Air upsell] step down failed:', err);
      if (removed && !added) await restore(restoreLines);
      track('nobl_air_upsell_failed', Object.assign({}, data, { error: String((err && err.message) || err) }));
      const cart = await fetchCart().catch(() => null);
      await refreshDrawer(cart).catch(() => {});
      if (cart) {
        lastState = bundleState(cart);
        publishCartUpdate(cart);
      }
    } finally {
      busy = false;
      setCheckoutBusy(false);
      setStepDownBusy(false);
      themeLoadingBar(false);
    }
  }

  document.addEventListener('click', (event) => {
    const minus = event.target.closest('button[name="minus"]');
    if (!minus) return;

    const qtyWrapper = minus.closest('quantity-input');
    const input = qtyWrapper && qtyWrapper.querySelector('.quantity__input[data-key]');
    const root = upsellRoot();
    if (!input || !root || root.dataset.nauEnabled !== 'true') return;

    const from = parseInt(root.dataset.nauQty, 10);
    if (from !== 3 && from !== 5) return;
    if (!root.dataset.nauVariantId || !root.dataset.nauKaching) return;

    let keys = [];
    try { keys = JSON.parse(root.dataset.nauKeys || '[]'); } catch (e) { keys = []; }
    if (!keys.includes(input.dataset.key)) return; // not the NOBL Air bundle line

    // Stop the theme's -1 from running at all.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;

    const to = from === 5 ? 3 : 1;
    themeLoadingBar(true);
    setStepDownBusy(true, input, to);
    stepDown(root, keys, from, to);
  }, true);

  // Runs before the theme's product-form handler (capture phase).
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const offerEl = form && form.closest ? form.closest('[data-nau-offer]') : null;
    if (!offerEl) return;

    // Second pass: prep is done, let product-form handle the add.
    if (form.dataset.nauReady === 'true') {
      delete form.dataset.nauReady;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    busy = true;
    prepareUpgrade(form, offerEl);
  }, true);

  // Power Bank uses the normal quick-add form - only track the click here.
  document.addEventListener('submit', (event) => {
    const pb = event.target.closest('[data-nau-powerbank]');
    if (pb) track('nobl_air_upsell_clicked', payloadFrom(pb));
  }, true);

  function init() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(scheduleViewCheck).observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
      });
    }
    scheduleViewCheck();
    lastState = readInitialState();
    watchCartEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();